import React, { useState, useRef, useEffect } from 'react';
import { Store, ChevronDown, Check, Globe, Eye, Zap, Power } from 'lucide-react';
import { useOutlet } from '../../context/OutletContext';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import './OutletSelector.css';

const OutletSelector = ({ userRole = 'admin', showViewToggle = true }) => {
  const { outlets, selectedOutlet, selectOutlet, loading, viewMode, toggleViewMode } = useOutlet();
  const [isOpen, setIsOpen] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const dropdownRef = useRef(null);

  const closeReasons = [
    { value: 'out_of_stock', label: '📦 Out of Stock' },
    { value: 'closing_time', label: '🕐 Closing Time' },
    { value: 'staff_unavailable', label: '👥 Staff Unavailable' },
    { value: 'maintenance', label: '🔧 Maintenance' },
    { value: 'emergency', label: '🚨 Emergency' },
    { value: 'delivery_issues', label: '🚚 Delivery Issues' },
    { value: 'other', label: '📝 Other Reason' }
  ];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Alt+O to toggle dropdown
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.altKey && e.key === 'o') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Toggle outlet online/offline status
  const handleToggleStatus = async (e) => {
    e.stopPropagation();
    if (!selectedOutlet || togglingStatus) return;

    const currentStatus = selectedOutlet.settings?.shopOnline ?? false;

    // If turning OFF, show reason modal
    if (currentStatus) {
      setShowReasonModal(true);
      return;
    }

    // If turning ON, proceed directly
    await performStatusToggle(true, null);
  };

  // Actually perform the status toggle
  const performStatusToggle = async (newStatus, reason) => {
    if (!selectedOutlet) return;

    setTogglingStatus(true);
    try {
      const updateData = {
        'settings.shopOnline': newStatus,
        updatedAt: Timestamp.now()
      };

      // If closing, add reason and timestamp
      if (!newStatus && reason) {
        updateData['settings.closeReason'] = reason;
        updateData['settings.closedAt'] = Timestamp.now();
      }

      // If opening, clear close reason
      if (newStatus) {
        updateData['settings.closeReason'] = null;
        updateData['settings.closedAt'] = null;
      }

      await updateDoc(doc(db, 'outlets', selectedOutlet.id), updateData);
      console.log(`✅ ${selectedOutlet.name} is now ${newStatus ? 'ONLINE' : 'OFFLINE'}${reason ? ` (Reason: ${reason})` : ''}`);

      setShowReasonModal(false);
      setSelectedReason('');
    } catch (error) {
      console.error('❌ Error toggling outlet status:', error);
      alert('Failed to update outlet status');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleReasonSubmit = () => {
    if (!selectedReason) {
      alert('Please select a reason for closing');
      return;
    }
    performStatusToggle(false, selectedReason);
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="outlet-selector outlet-selector--loading">
        <Store size={18} />
        <span>Loading outlets...</span>
      </div>
    );
  }

  // Handle empty state
  if (!outlets || outlets.length === 0) {
    return (
      <div className="outlet-selector outlet-selector--empty">
        <Store size={18} />
        <span>No outlets available</span>
      </div>
    );
  }

  const handleSelectOutlet = (outletId) => {
    selectOutlet(outletId);
    setIsOpen(false);
  };

  const isOnline = selectedOutlet?.settings?.shopOnline ?? false;

  return (
    <div className="outlet-selector-wrapper">
      {/* Quick Status Toggle Button (Admin only) */}
      {showViewToggle && userRole === 'admin' && selectedOutlet && viewMode !== 'all' && (
        <button
          className={`outlet-status-toggle ${isOnline ? 'outlet-status-toggle--online' : 'outlet-status-toggle--offline'}`}
          onClick={handleToggleStatus}
          disabled={togglingStatus}
          title={`Click to turn ${isOnline ? 'OFF' : 'ON'}`}
        >
          {togglingStatus ? (
            <>
              <Power size={16} className="animate-spin" />
              <span className="status-label">Updating...</span>
            </>
          ) : isOnline ? (
            <>
              <Zap size={16} className="animate-pulse" />
              <span className="status-label">ONLINE</span>
            </>
          ) : (
            <>
              <Power size={16} />
              <span className="status-label">OFFLINE</span>
            </>
          )}
        </button>
      )}

      {/* View Mode Toggle (Admin only) */}
      {showViewToggle && userRole === 'admin' && (
        <button
          className={`view-mode-toggle ${viewMode === 'all' ? 'view-mode-toggle--active' : ''}`}
          onClick={toggleViewMode}
          title={viewMode === 'all' ? 'Viewing All Outlets' : 'Viewing Single Outlet'}
        >
          {viewMode === 'all' ? (
            <>
              <Globe size={16} />
              <span className="view-mode-label">All Outlets</span>
            </>
          ) : (
            <>
              <Eye size={16} />
              <span className="view-mode-label">Single</span>
            </>
          )}
        </button>
      )}

      <div className="outlet-selector" ref={dropdownRef}>
      <button
        className="outlet-selector__trigger"
        onClick={() => setIsOpen(!isOpen)}
        title="Switch outlet (Alt+O)"
      >
        <Store size={18} className="outlet-selector__icon" />
        <div className="outlet-selector__current">
          <span className="outlet-selector__label">
            Current Outlet
            {selectedOutlet && viewMode !== 'all' && (
              <span className={`outlet-status-dot ${isOnline ? 'outlet-status-dot--online' : 'outlet-status-dot--offline'}`}
                    title={isOnline ? 'Online' : 'Offline'}>
                •
              </span>
            )}
          </span>
          <span className="outlet-selector__name">
            {viewMode === 'all' ? 'All Outlets' : (selectedOutlet ? selectedOutlet.name : 'Select Outlet')}
          </span>
        </div>
        <ChevronDown
          size={18}
          className={`outlet-selector__arrow ${isOpen ? 'outlet-selector__arrow--open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="outlet-selector__dropdown">
          <div className="outlet-selector__header">
            <Store size={16} />
            <span>Select Outlet</span>
          </div>

          <div className="outlet-selector__list">
            {outlets.map((outlet) => {
              const outletIsOnline = outlet.settings?.shopOnline ?? false;
              return (
                <button
                  key={outlet.id}
                  className={`outlet-selector__item ${
                    selectedOutlet?.id === outlet.id ? 'outlet-selector__item--active' : ''
                  }`}
                  onClick={() => handleSelectOutlet(outlet.id)}
                >
                  <div className="outlet-selector__item-info">
                    <div className="outlet-selector__item-name-wrapper">
                      <span className="outlet-selector__item-name">{outlet.name}</span>
                      <span className={`outlet-status-badge ${outletIsOnline ? 'outlet-status-badge--online' : 'outlet-status-badge--offline'}`}>
                        {outletIsOnline ? '🟢 Online' : '🔴 Offline'}
                      </span>
                    </div>
                    {outlet.address && (
                      <span className="outlet-selector__item-address">{outlet.address}</span>
                    )}
                  </div>
                  {selectedOutlet?.id === outlet.id && (
                    <Check size={16} className="outlet-selector__check" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="outlet-selector__footer">
            <span className="outlet-selector__count">
              {outlets.length} outlet{outlets.length !== 1 ? 's' : ''} available
            </span>
            <span className="outlet-selector__shortcut">Alt+O</span>
          </div>
        </div>
      )}
      </div>

      {/* Reason Modal for Closing */}
      {showReasonModal && (
        <div className="reason-modal-overlay" onClick={() => setShowReasonModal(false)}>
          <div className="reason-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reason-modal-header">
              <h3>🔴 Close Outlet</h3>
              <button className="reason-modal-close" onClick={() => setShowReasonModal(false)}>
                ×
              </button>
            </div>

            <div className="reason-modal-body">
              <p className="reason-modal-description">
                Why are you closing <strong>{selectedOutlet?.name}</strong>?
              </p>

              <div className="reason-options">
                {closeReasons.map((reason) => (
                  <button
                    key={reason.value}
                    className={`reason-option ${selectedReason === reason.value ? 'reason-option--selected' : ''}`}
                    onClick={() => setSelectedReason(reason.value)}
                  >
                    <span className="reason-label">{reason.label}</span>
                    {selectedReason === reason.value && <Check size={18} className="reason-check" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="reason-modal-footer">
              <button
                className="reason-btn reason-btn--cancel"
                onClick={() => {
                  setShowReasonModal(false);
                  setSelectedReason('');
                }}
              >
                Cancel
              </button>
              <button
                className="reason-btn reason-btn--close"
                onClick={handleReasonSubmit}
                disabled={!selectedReason || togglingStatus}
              >
                {togglingStatus ? 'Closing...' : 'Close Outlet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutletSelector;
